import React, {CSSProperties, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, Grid, IconButton, Stack} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/ReportGmailerrorred";
import {Field} from "../../types/domain/field";
import {useCrops} from "../../hooks/cropsHooks";
import {FieldsTableHeader} from "../fields/FieldsTableHeader";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {swallowException} from "../../utils/appUtils";
import {useFields} from "../../hooks/fieldsHooks";
import {FieldStatus} from "../../types/domain/fieldStatus";
import {getLatestCropId} from "../../utils/fieldUtils";
import {FieldTableCell} from "../fields/FieldTableCell";
import {DeleteIcon} from "../icons/DeleteIcon";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {getFieldAreaFormatted} from "../../utils/measureSystemUtils";

interface FieldCompProps {
    orderId: string;
    companyId: string;
}

export const OrderEditFieldsTable: React.FC<FieldCompProps> = ({orderId, companyId}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const fieldManagement = useFieldManagement();
    const [selectedField, setSelectedField] = useState<Field | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
    const fieldsState = useFields({companyId, orderId});
    const {cropsById} = useCrops();

    const confirmationMessage = useMemo(() => {
        if (selectedField) {
            return t("orders.order_edit_fields_table.delete_confirmation", {name: selectedField.name});
        } else {
            return "";
        }
    }, [selectedField, t]);

    const handleFieldDelete = (field: Field) => {
        setSelectedField(field);
        setDeleteConfirm(true);
    };

    const handleCancelDelete = () => {
        setSelectedField(null);
        setDeleteConfirm(false);
    };

    const handleConfirmDelete = () => {
        fieldManagement
            .delete(companyId, selectedField!.id)
            .then(() => {
                fieldsState.refresh();
            })
            .catch(swallowException);
        setSelectedField(null);
        setDeleteConfirm(false);
    };

    const fieldHeaderItems = [
        t("orders.order_edit_fields_table.header_field"),
        t("orders.order_edit_fields_table.header_ready"),
        t("orders.order_edit_fields_table.header_crop"),
        t("orders.order_edit_fields_table.header_area"),
    ];

    const headerBreakpoints = [5, 2, 3, 4];

    return (
        <div>
            <div style={{...disableOnTrue(deleteConfirm), ...dimOnTrue(deleteConfirm)}}>
                <FieldsTableHeader
                    items={fieldHeaderItems}
                    columns={14}
                    breakpoints={headerBreakpoints}
                ></FieldsTableHeader>
                <Box bgcolor="white">
                    {fieldsState.items.map((field, index) => (
                        <Grid container sx={{p: 1}} key={index} alignItems="center" columns={14}>
                            <Grid item xs={headerBreakpoints[0]} zeroMinWidth>
                                <FieldTableCell color="primary">{field.name}</FieldTableCell>
                            </Grid>
                            <Grid item xs={headerBreakpoints[1]}>
                                <FieldTableCell>
                                    <CheckIcon color={getFinalColor(field)} />
                                </FieldTableCell>
                            </Grid>
                            <Grid item xs={headerBreakpoints[2]} zeroMinWidth>
                                <FieldTableCell>
                                    {getLatestCropId(field) ? cropsById[getLatestCropId(field)!].name : ""}
                                </FieldTableCell>
                            </Grid>
                            <Grid item xs={headerBreakpoints[3]}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <FieldTableCell>
                                        {getFieldAreaFormatted(field, msUtils)} {msUtils.getAreaUnits()}
                                    </FieldTableCell>
                                    <IconButton color="error" onClick={() => handleFieldDelete(field)}>
                                        <DeleteIcon sx={{stroke: "#EA463C", width: "1.1rem", height: "1.1rem"}} />
                                    </IconButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            </div>
            {/*CONFIRMING PART*/}
            {deleteConfirm && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        textTransform: "none",
                    }}
                >
                    <WarningIcon color="error" />
                    <Box sx={{ml: "0.75rem"}}>{confirmationMessage}</Box>
                    <Button onClick={handleCancelDelete}>{t("dialogs.actions.cancel")}</Button>
                    <Button color="error" onClick={handleConfirmDelete}>
                        {t("dialogs.actions.confirm")}
                    </Button>
                </Box>
            )}
        </div>
    );
};

const dimOnTrue = (flag: boolean): CSSProperties => {
    return {
        opacity: flag ? 0.5 : 1,
    };
};

const disableOnTrue = (flag: boolean): CSSProperties => {
    return {
        pointerEvents: flag ? "none" : "initial",
    };
};

const getFinalColor = (field: Field): "primary" | "disabled" => {
    return field.status === FieldStatus.FINAL ? "primary" : "disabled";
};
